import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'primereact/button';
import { defineMessages, useIntl } from 'react-intl';
import { buildSubscriptionPeriodText, getFormattedDate, getNumberOfEmployeesLabel } from '../../utils/AppUtil';
import { SubscriptionStatus } from '../../shared/Constants';

const i18nMessages = defineMessages({
  payNow: {
    id: 'account.generic.pay-now',
    defaultMessage: 'Pay now',
  },
  renew: {
    id: 'account.generic.renew',
    defaultMessage: 'Renew',
  },
  noExistingSubscription: {
    id: 'business-center.no-existing-subscription',
    defaultMessage: 'You do not have any subscriptions you can renew. You can only buy a new subscription',
  },
  subscriptionStopped: {
    id: 'business-center.subscription-stopped',
    defaultMessage: 'Your current subscription has been stopped',
  },
  purchasedOn: {
    id: 'business-center.purchased-on',
    defaultMessage: 'It was purchased on:',
  },
  endedOn: {
    id: 'business-center.ended-on',
    defaultMessage: 'It ended on:',
  },
  numberOfEmployeesIncluded: {
    id: 'account.subscription.number-of-employees-included',
    defaultMessage: 'Number of employees included:',
  },
  prePaidSubscriptionPeriod: {
    id: 'account.subscription.pre-paid-subscription-period',
    defaultMessage: 'Pre-paid subscription period:',
  },
  buyOrRenew: {
    id: 'account.subscription.buy-or-renew',
    defaultMessage: 'You can either buy a new subscription or renew the existing one',
  },
  month: {
    id: 'account.subscription.month',
    defaultMessage: 'month',
  },
  months: {
    id: 'account.subscription.months',
    defaultMessage: 'months',
  },
  error: {
    id: 'account.subscription.error',
    defaultMessage: 'There was an error displaying the account information you chose',
  },
});

const RenewSubscription = ({ account, renewSubscription, redirectToBuySubscriptionPage }) => {
  const { formatMessage, formatDate } = useIntl();

  if (!account) {
    return (
      <div className="align-center">
        <h4>{formatMessage(i18nMessages.error)}</h4>
      </div>
    );
  }

  const {
    status,
    subscriptionInformation: {
      currentSubscription,
    },
  } = account;

  if (!currentSubscription || status === SubscriptionStatus.CANCELED) {
    return (
      <div className="align-center">
        <h4>{formatMessage(i18nMessages.noExistingSubscription)}</h4>
        <Button
          type="button"
          label={formatMessage(i18nMessages.payNow)}
          onClick={redirectToBuySubscriptionPage}
        />
      </div>
    );
  }

  const {
    purchasedOn, endedOn, numberOfEmployees, duration,
  } = currentSubscription;

  const numberOfEmployeesIncluded = getNumberOfEmployeesLabel(numberOfEmployees);
  const subscriptionPeriodText = buildSubscriptionPeriodText(
    formatMessage(i18nMessages.months),
    formatMessage(i18nMessages.month),
    duration,
  );

  return (
    <div>
      <h4>{formatMessage(i18nMessages.subscriptionStopped)}</h4>
      <div className="existing-subscription-info">
        <div>
          <strong>{formatMessage(i18nMessages.purchasedOn)}</strong>
        </div>
        <div>{getFormattedDate(purchasedOn, formatDate)}</div>
      </div>
      <br />
      <div className="existing-subscription-info">
        <div>
          <strong>{formatMessage(i18nMessages.endedOn)}</strong>
        </div>
        <div>{getFormattedDate(endedOn, formatDate)}</div>
      </div>
      <br />
      <div className="existing-subscription-info">
        <div>
          <strong>{formatMessage(i18nMessages.numberOfEmployeesIncluded)}</strong>
        </div>
        <div>{numberOfEmployeesIncluded}</div>
      </div>
      <br />
      <div className="existing-subscription-info">
        <div>
          <strong>{formatMessage(i18nMessages.prePaidSubscriptionPeriod)}</strong>
        </div>
        <div>{subscriptionPeriodText}</div>
      </div>
      <br />
      <h4 className="align-center">{formatMessage(i18nMessages.buyOrRenew)}</h4>
      <div className="align-center">
        <Button
          type="button"
          label={formatMessage(i18nMessages.payNow)}
          onClick={redirectToBuySubscriptionPage}
        />
        <Button type="button" label={formatMessage(i18nMessages.renew)} onClick={renewSubscription} />
      </div>
    </div>
  );
};

RenewSubscription.propTypes = {
  account: PropTypes.objectOf(PropTypes.any).isRequired,
  renewSubscription: PropTypes.func.isRequired,
  redirectToBuySubscriptionPage: PropTypes.func.isRequired,
};

export default RenewSubscription;
